import React, { useState } from "react";
import { ChevronDown, Download, HelpCircle, Sun } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { EaChartCard, EaKpiCard } from "./EaUi";
import { COLORS, MONTHS_IT, fmtEur, fmtPower, fmtQty } from "./eaFormat";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

function Faq({ c, open, setOpen }) {
  return (
    <div id="ea-self-faq" className="rounded-lg border border-slate-200 bg-white scroll-mt-24" data-testid="ea-self-faq">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-slate-700" data-testid="ea-self-faq-toggle">
        <span className="inline-flex items-center gap-2"><HelpCircle size={15} className="text-violet-600" /> Come calcoliamo il costo evitato stimato?</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="px-5 pb-3 text-xs text-slate-600 space-y-1.5 list-decimal" data-testid="ea-self-faq-body">
          <li><b>Energia autoconsumata:</b> autoproduzione rinnovabile + PPA on-site inseriti mese per mese ({fmtQty(c.kwh / 1000, 0, "MWh")}). Con il solo totale annuo lo ripartiamo sui mesi con il profilo tipico del fotovoltaico in Italia.</li>
          <li><b>Solo ore di sole:</b> l'impianto produce solo nelle ore di irraggiamento. Per ogni mese costruiamo un profilo orario a campana tra alba e tramonto (più produzione a mezzogiorno, zero di notte).</li>
          <li><b>PUN ore solari (automatico):</b> PUN orario di ciascun mese, preso dai dati di mercato caricati in piattaforma, pesato su quel profilo = <b>{fmtQty(c.solar_pun, 1, "€/MWh")}</b> nel periodo. Si aggiorna da solo al cambio di anno e con i nuovi prezzi di mercato. Componente energia: {fmtEur(c.energy_eur)}.</li>
          <li><b>Altre componenti della bolletta:</b> {c.other_eur_mwh != null ? <><b>{fmtQty(c.other_eur_mwh, 1, "€/MWh")}</b> ({c.other_source === "manuale" ? "valore inserito manualmente in Sostenibilità → Dati e fattori" : "dal controllo fatture: costo medio fatturato dei POD selezionati meno PUN medio dello stesso anno"}): rete, oneri di sistema, spread e accise non pagati sull'energia autoconsumata. Il valore si può sostituire in Sostenibilità → Dati e fattori.</> : "non disponibili (mancano fatture): il costo evitato comprende solo la componente energia."}</li>
          <li><b>Costo PPA:</b> per il PPA on-site si sottrae l'energia pagata al produttore = MWh PPA × prezzo PPA €/MWh {c.ppa_cost_eur ? `(${fmtEur(c.ppa_cost_eur)})` : "(prezzo non inserito)"}.</li>
          <li><b>Formula:</b> risparmio netto = Σ mesi [MWh autoconsumati × (PUN ore solari + altre componenti)] − costo PPA.</li>
          <li><b>Limiti:</b> è una stima. Non considera l'energia immessa in rete, gli incentivi, le quote fisse e la potenza.</li>
        </ol>
      )}
    </div>
  );
}

function SelfChart({ rows, download }) {
  const data = rows.filter((r) => r.kwh).map((r) => ({ label: MONTHS_IT[r.month - 1], mwh: r.kwh / 1000, avoided: r.avoided_eur, net: r.net_eur }));
  return (
    <EaChartCard title="Autoconsumo e risparmi mensili" subtitle="Barre = MWh autoconsumati · linee = costo evitato e risparmio netto (€)" testid="ea-self-chart"
      right={download && <button onClick={download} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="ea-self-xlsx"><Download size={15} /> Scarica Excel</button>}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="label" {...axis} />
            <YAxis yAxisId="l" {...axis} width={55} tickFormatter={(v) => fmtQty(v, 0)} />
            <YAxis yAxisId="r" orientation="right" {...axis} width={80} tickFormatter={(v) => fmtEur(v)} />
            <Tooltip formatter={(v, n) => [n === "Autoconsumo" ? fmtQty(v, 1, "MWh") : fmtEur(v), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="mwh" name="Autoconsumo" fill={COLORS.power} radius={[3, 3, 0, 0]} />
            <Line yAxisId="r" dataKey="avoided" name="Costo evitato" stroke={COLORS.powerDark} strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="r" dataKey="net" name="Risparmio netto" stroke="#94A3B8" strokeDasharray="5 3" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </EaChartCard>
  );
}

function Amounts({ sc, onFaq }) {
  return (
    <div className="mt-3 space-y-2">
      <div className="rounded-xl bg-violet-50 border border-violet-200 px-4 py-2.5 flex items-center gap-3" data-testid="ea-self-avoided">
        <Sun size={20} className="text-amber-500" />
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700">Costo evitato stimato</div>
          <div className="font-display text-2xl font-bold text-violet-900 font-mono-nums" data-testid="ea-self-avoided-value">{fmtEur(sc.avoided_eur)}</div>
        </div>
      </div>
      <div className="rounded-xl text-white px-4 py-2.5" style={{ background: "#33116F" }} data-testid="ea-self-net">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-200">Risparmio netto stimato</div>
        <div className="font-display text-2xl font-bold font-mono-nums" data-testid="ea-self-net-value">{fmtEur(sc.net_eur)}</div>
        <div className="text-[11px] text-slate-300">{sc.ppa_cost_eur ? `Costo evitato − costo PPA on-site ${fmtEur(sc.ppa_cost_eur)}` : "Nessun costo PPA inserito"}{sc.ppa_unpriced_mwh ? ` · ${fmtQty(sc.ppa_unpriced_mwh, 0, "MWh")} PPA senza prezzo` : ""}</div>
      </div>
      <button onClick={onFaq} className="text-xs font-semibold text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="ea-self-faq-link"><HelpCircle size={13} /> Come calcoliamo il costo evitato?</button>
    </div>
  );
}

export function EaSelfSection({ sc, band, unit, include, setInclude, params }) {
  const download = async () => {
    try {
      const r = await api.get("/ea/overview/self-consumption.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `autoconsumo_risparmi_${params.year || ""}.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };
  const priced = sc.avoided_eur != null;
  const [faq, setFaq] = useState(false);
  const openFaq = () => { setFaq(true); setTimeout(() => document.getElementById("ea-self-faq")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  return (
    <section className="space-y-4" data-testid="ea-sec-self">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <EaKpiCard band={band} label="Autoproduzione e autoconsumo" value={fmtPower(sc.kwh, unit)} testid="ea-kpi-self"
          sub={`${fmtQty(sc.share_pct, 1, "%")} dei consumi totali · autoproduzione ${fmtPower(sc.by_source.self, unit)} · PPA on-site ${fmtPower(sc.by_source.ppa_onsite, unit)}`}>
          {priced && <Amounts sc={sc} onFaq={openFaq} />}
          <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={include} onCheckedChange={setInclude} className="data-[state=checked]:bg-[#8B32FB]" data-testid="ea-self-include-toggle" /> Includi l'autoconsumo nei consumi
          </label>
        </EaKpiCard>
        <div className="xl:col-span-2"><SelfChart rows={sc.monthly} download={priced ? download : null} /></div>
      </div>
      {priced && <Faq c={sc} open={faq} setOpen={setFaq} />}
    </section>
  );
}
